import {
  ChangeDetectionStrategy,
  Component,
  effect,
  EventEmitter,
  Input,
  input,
  InputSignal,
  OnInit,
  Output,
  signal
} from '@angular/core';
import { NgApexchartsModule } from 'ng-apexcharts';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { BaseDashboardChartComponent, FilterOption } from '../base-dashboard-chart.component';
import { ChartCardComponent } from "../chart-card/chart-card.component";
import { SpinnerComponent } from '../../spinner/spinner.component';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {TranslatePipe} from "@ngx-translate/core";

@Component({
  selector: 'co-bar-plot',
  imports: [NgApexchartsModule, SelectModule, FormsModule, ChartCardComponent, SpinnerComponent, TranslatePipe],
  templateUrl: './bar-plot.component.html',
  styleUrl: './bar-plot.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class BarPlotComponent extends BaseDashboardChartComponent implements OnInit {

  data: InputSignal<any[]> = input.required();
  hoverLabels: InputSignal<any[]> = input.required();
  isHorizontalBar: InputSignal<boolean> = input.required();
  chartOptions: any = signal({});
  isXLabels: InputSignal<boolean> = input(false);
  isYLabels: InputSignal<boolean> = input(false);
  isLegend: InputSignal<boolean> = input(false);
  xAxisTitle = input<string>('');
  yAxisTitle = input<string>('');
  isFirstLoad = signal(true);
  chartReady = input.required<boolean>();

  private langChangeSub?: Subscription;

  tooltipFormatter: InputSignal<(label: any, seriesIndex: number) => string> = input.required();

  @Input() ChartStats?: string;
  constructor(private translate: TranslateService) {
    super();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.updateChartOptions();
    });
    effect(() => {
      if (this.data().length > 0) {
        const data = this.data()
        this.updateChartOptions();
      }
    })
  }
  ngOnDestroy() {
    this.langChangeSub?.unsubscribe();
  }
  ngOnInit(): void {
    // this.updateChartOptions();
  }

  updateChartOptions(): void {
    const currentSeries = {
      name: this.translate.instant('ICD10.TOOLTIP.CURRENT'),
      data: this.data().map(item => item.currentDisplayData)
    };

    const hasHistoricalData = this.data().some(
      item => item.currentDisplayData !== item.historicalDisplayData && item.historicalDisplayData !== 0
    );

    const series = [currentSeries];

    if (hasHistoricalData) {
      series.push({
        name: this.translate.instant('ICD10.TOOLTIP.HISTORICAL'),
        data: this.data().map(item => item.historicalDisplayData)
      });
    }

    this.chartOptions.set({
      series: series,
      chart: {
        width: '100%',
        height: 600,
        type: 'bar',
        animations: { enabled: false },
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: true,
          allowMouseWheelZoom: false,
          zoomedArea: {
            fill: { color: '#90CAF9', opacity: 0.4 },
            stroke: { color: '#0D47A1', opacity: 0.4, width: 1 },
          },
        },
        toolbar: {
          show: true,
          tools: { download: true, zoom: true, zoomin: false, zoomout: false, pan: false, reset: true },
        }
      },
      plotOptions: {
        bar: {
          distributed: false,
          horizontal: this.isHorizontalBar(),
          borderRadius: 5,
        },
      },
      dataLabels: {
        enabled: true,
        position: 'center',
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
        },
        formatter: (val: number) => this.formatNumberShort(val),
      },
      stroke: {
        show: true,
        width: 2,
        colors: this.getColors(),
      },
      colors: this.getColors(),
      xaxis: {
        categories: this.data().map(item => item.title),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          show: this.isXLabels(),
          formatter: (val: number) => this.formatNumberShort(val)
        },
        title: { text: this.xAxisTitle() }
      },
      yaxis: {
        labels: {
          show: this.isYLabels(),
          formatter: (val: number) => this.formatNumberShort(val)
        },
        title: { text: this.yAxisTitle() }
      },
      legend: {
        show: this.isLegend(),
        position: 'bottom',
        floating: false,
        labels: { colors: undefined, useSeriesColors: false },
        markers: { width: 12, height: 12 },
        itemMargin: { horizontal: 5, vertical: 5 },
      },
      tooltip: {
        enabled: true,
        intersect: true,
        shared: false,
        followCursor: true,
        custom: (opts: any) => {
          const label = this.hoverLabels()[opts.dataPointIndex];
          const formatter = this.tooltipFormatter();
          if (!label || !formatter) return '';
          return formatter(label, opts.seriesIndex);
        }
      }
    });
  }


  getColors(color: string = '') {
    if (color == '') {
      return [
        '#125773',
        '#98BDFF',
        '#7DA0FA',
        '#7978E9',
        '#125773',
        '#27CDF2',
        '#488A99',
        '#C6D936',
      ];
    } else return [color];
  }

  formatNumberShort(n: number): string {
    if (typeof n !== 'number') {
      return n;
    }

    if (n >= 1000000000) return (n / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
    if (n >= 1000000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    return n.toString();
  }
}

export type BarChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  fill: ApexFill;
  tooltip: ApexTooltip;
  stroke: ApexStroke;
  legend: ApexLegend;
};
